import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header.jsx';
import { EstadoBadge } from '../components/ui/EstadoBadge.jsx';
import { ComboboxSelect } from '../components/ui/ComboboxSelect.jsx';
import { LeadForm } from '../features/leads/LeadForm.jsx';
import { useLead, useUpdateLead, useReasignarLead, useDeleteLead } from '../features/leads/useLeads.js';
import { useCambiarEstado, useHistorialEstados, TODOS_LOS_ESTADOS } from '../features/estados/useEstados.js';
import { useCreateRecordatorio, useUpdateRecordatorio } from '../features/recordatorios/useRecordatorios.js';
import { useVendedores } from '../features/usuarios/useUsuarios.js';
import { useAuthStore } from '../stores/authStore.js';
import { formatFecha } from '../lib/formatters.js';

export default function LeadDetallePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const usuario = useAuthStore((s) => s.usuario);
  const esViewer = usuario?.rol === 'viewer';
  const esAdmin = usuario?.rol === 'admin';

  const { data: lead, isLoading } = useLead(id);
  const { data: historial = [] } = useHistorialEstados(id);
  const cambiarEstado = useCambiarEstado(id);
  const update = useUpdateLead(id);
  const reasignar = useReasignarLead(id);
  const deleteLead = useDeleteLead();
  const vendedores = useVendedores();

  const [editando, setEditando] = useState(false);
  const [confirmandoEliminar, setConfirmandoEliminar] = useState(false);

  if (isLoading) return <p className="p-6 font-['Inter'] text-[#6B7280]">Cargando...</p>;
  if (!lead) return null;

  function handleEditar(data) {
    update.mutate(data, { onSuccess: () => setEditando(false) });
  }

  return (
    <div>
      <Header
        title={lead.nombre_apellido}
        back={{ label: 'Volver', onClick: () => navigate(-1) }}
      />
      <div className="p-6 max-w-6xl mx-auto flex flex-col gap-5">

        {/* Fila superior */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">

          {/* Datos principales */}
          <div className="md:col-span-2 bg-white rounded-lg border border-gray-100 shadow-sm p-6 flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <EstadoBadge estado={lead.estado} />
                <span className="font-['Inter'] text-[#6B7280] text-sm">{lead.canal_ingesta}</span>
                <span className="font-['Inter'] text-[#6B7280] text-sm">{lead.mes_periodo}</span>
              </div>
              {!esViewer && !editando && (
                <button
                  onClick={() => setEditando(true)}
                  className="text-[#0D2B5C] hover:text-[#1E9BE8] font-['Raleway'] font-semibold text-sm transition-colors"
                >
                  Editar
                </button>
              )}
            </div>

            {editando ? (
              <LeadForm
                defaultValues={{
                  nombre_apellido: lead.nombre_apellido,
                  telefono: lead.telefono,
                  email: lead.email ?? '',
                  dni: lead.dni ?? '',
                  fecha_nac: lead.fecha_nac ? lead.fecha_nac.slice(0, 10) : '',
                  origen: lead.origen,
                  abono: lead.abono ?? undefined,
                  verificado: lead.verificado,
                  observacion: lead.observacion ?? '',
                }}
                onSubmit={handleEditar}
                isPending={update.isPending}
                onCancel={() => setEditando(false)}
              />
            ) : (
              <div className="grid grid-cols-2 gap-y-3 gap-x-6 text-sm font-['Inter']">
                <Dato label="Teléfono" value={lead.telefono} />
                <Dato label="Email" value={lead.email} />
                <Dato label="DNI" value={lead.dni} />
                <Dato label="Fecha nac." value={formatFecha(lead.fecha_nac)} />
                <Dato label="Origen" value={lead.origen} />
                <Dato label="Abono" value={lead.abono} />
                <Dato label="Verificado" value={lead.verificado ? 'Sí' : 'No'} />
                <Dato label="Ingreso" value={formatFecha(lead.fecha_ingreso)} />
                {!esViewer && (
                  <div className="col-span-2 pt-2 border-t border-gray-100">
                    <ObservacionInline
                      value={lead.observacion ?? ''}
                      onSave={(val) => update.mutate({ observacion: val })}
                    />
                  </div>
                )}
                {esViewer && lead.observacion && (
                  <div className="col-span-2 pt-2 border-t border-gray-100">
                    <span className="text-[#6B7280]">Observación: </span>{lead.observacion}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Panel derecho */}
          <div className="flex flex-col gap-4">

            {/* Cambio de estado */}
            {!esViewer && (
              <div className="bg-white rounded-lg border border-gray-100 shadow-sm p-5 flex flex-col gap-3">
                <h2 className="font-['Raleway'] font-semibold text-[#0D2B5C] text-sm">Cambiar estado</h2>
                <ComboboxSelect
                  options={TODOS_LOS_ESTADOS.map((e) => ({ value: e, label: e }))}
                  value={lead.estado}
                  onChange={(estado) => cambiarEstado.mutate(estado)}
                />
                {cambiarEstado.isPending && (
                  <p className="text-xs text-[#6B7280] font-['Inter']">Guardando...</p>
                )}
              </div>
            )}

            {/* Reasignar (solo admin) */}
            {esAdmin && (
              <div className="bg-white rounded-lg border border-gray-100 shadow-sm p-5 flex flex-col gap-3">
                <h2 className="font-['Raleway'] font-semibold text-[#0D2B5C] text-sm">Reasignar vendedor</h2>
                <ComboboxSelect
                  options={vendedores.map((v) => ({ value: v.id, label: v.nombre }))}
                  value={lead.vendedor_id ?? ''}
                  onChange={(vendedor_id) => reasignar.mutate(vendedor_id)}
                  placeholder="Seleccionar vendedor..."
                />
              </div>
            )}

            {/* Eliminar (solo admin) */}
            {esAdmin && (
              <div className="bg-white rounded-lg border border-gray-100 shadow-sm p-5 flex flex-col gap-3">
                {confirmandoEliminar ? (
                  <>
                    <p className="font-['Inter'] text-sm text-[#0D2B5C]">¿Eliminar este lead? Esta acción no se puede deshacer.</p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => deleteLead.mutate(id, { onSuccess: () => navigate('/leads') })}
                        disabled={deleteLead.isPending}
                        className="flex-1 bg-red-600 hover:bg-red-700 text-white font-['Raleway'] font-bold text-sm px-4 py-2 rounded-md transition-colors disabled:opacity-50"
                      >
                        {deleteLead.isPending ? 'Eliminando...' : 'Sí, eliminar'}
                      </button>
                      <button
                        onClick={() => setConfirmandoEliminar(false)}
                        className="flex-1 border border-gray-200 text-[#6B7280] font-['Raleway'] text-sm px-4 py-2 rounded-md hover:bg-gray-50 transition-colors"
                      >
                        Cancelar
                      </button>
                    </div>
                  </>
                ) : (
                  <button
                    onClick={() => setConfirmandoEliminar(true)}
                    className="text-red-600 hover:text-red-700 font-['Raleway'] font-semibold text-sm transition-colors text-left"
                  >
                    Eliminar lead
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Recordatorios */}
        {!esViewer && (
          <RecordatoriosLead leadId={id} recordatorios={lead.recordatorios ?? []} />
        )}

        {/* Historial */}
        <div className="bg-white rounded-lg border border-gray-100 shadow-sm p-6">
          <h2 className="font-['Raleway'] font-semibold text-[#0D2B5C] mb-4">Historial de estados</h2>
          {historial.length === 0 ? (
            <p className="text-sm font-['Inter'] text-[#6B7280]">Sin cambios registrados.</p>
          ) : (
            <div className="flex flex-col gap-2">
              {historial.map((h) => (
                <div key={h.id} className="flex items-center gap-3 text-sm font-['Inter'] text-[#6B7280]">
                  <span className="w-32 shrink-0">{formatFecha(h.created_at)}</span>
                  <EstadoBadge estado={h.estado_anterior} />
                  <span>→</span>
                  <EstadoBadge estado={h.estado_nuevo} />
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

function Dato({ label, value }) {
  return (
    <div>
      <span className="text-[#6B7280]">{label}: </span>
      <span className="text-[#0D2B5C]">{value ?? '—'}</span>
    </div>
  );
}

function RecordatoriosLead({ leadId, recordatorios }) {
  const crear = useCreateRecordatorio();
  const [fecha, setFecha] = useState('');
  const [nota, setNota] = useState('');

  function handleCrear(e) {
    e.preventDefault();
    if (!fecha) return;
    crear.mutate(
      { lead_id: leadId, fecha_compromiso: fecha, nota: nota || undefined },
      { onSuccess: () => { setFecha(''); setNota(''); } }
    );
  }

  const estadoStyle = {
    activo:       'bg-blue-100 text-blue-700',
    vencido:      'bg-red-100 text-red-700',
    resuelto:     'bg-green-100 text-green-700',
    reprogramado: 'bg-yellow-100 text-yellow-700',
  };

  return (
    <div className="bg-white rounded-lg border border-gray-100 shadow-sm p-6 flex flex-col gap-4">
      <h2 className="font-['Raleway'] font-semibold text-[#0D2B5C]">Recordatorios</h2>

      {recordatorios.length > 0 && (
        <div className="flex flex-col gap-2">
          {recordatorios.map((r) => (
            <RecordatorioLineaLead key={r.id} r={r} estadoStyle={estadoStyle} />
          ))}
        </div>
      )}

      <form onSubmit={handleCrear} className="flex flex-wrap items-end gap-3 pt-2 border-t border-gray-100">
        <div className="flex flex-col gap-1">
          <label className="font-['Raleway'] text-[#1E9BE8] text-xs font-semibold tracking-widest uppercase">Fecha</label>
          <input
            type="date"
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
            required
            className="border border-gray-200 rounded-md px-3 py-2 text-sm font-['Inter'] focus:outline-none focus:ring-2 focus:ring-[#0D2B5C]"
          />
        </div>
        <div className="flex flex-col gap-1 flex-1 min-w-[160px]">
          <label className="font-['Raleway'] text-[#1E9BE8] text-xs font-semibold tracking-widest uppercase">Nota</label>
          <input
            type="text"
            value={nota}
            onChange={(e) => setNota(e.target.value)}
            placeholder="Opcional..."
            className="border border-gray-200 rounded-md px-3 py-2 text-sm font-['Inter'] focus:outline-none focus:ring-2 focus:ring-[#0D2B5C]"
          />
        </div>
        <button
          type="submit"
          disabled={!fecha || crear.isPending}
          className="bg-[#1E9BE8] hover:bg-[#1780C5] text-white font-['Raleway'] font-bold text-sm px-5 py-2 rounded-md transition-colors disabled:opacity-50 min-h-[42px]"
        >
          + Agregar
        </button>
      </form>
    </div>
  );
}

function RecordatorioLineaLead({ r, estadoStyle }) {
  const update = useUpdateRecordatorio(r.id);
  const [reprogramando, setReprogramando] = useState(false);
  const [nuevaFecha, setNuevaFecha] = useState('');

  function handleReprogramar(e) {
    e.preventDefault();
    if (!nuevaFecha) return;
    update.mutate({ estado: 'reprogramado', fecha_compromiso: nuevaFecha }, {
      onSuccess: () => { setReprogramando(false); setNuevaFecha(''); },
    });
  }

  return (
    <div className={`flex items-center gap-3 text-sm font-['Inter'] p-2 rounded-md ${r.estado === 'vencido' ? 'bg-red-50' : 'bg-gray-50'}`}>
      <span className="text-[#6B7280] w-24 shrink-0">{formatFecha(r.fecha_compromiso)}</span>
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium shrink-0 ${estadoStyle[r.estado] ?? 'bg-gray-100 text-gray-700'}`}>
        {r.estado}
      </span>
      {r.nota && <span className="text-[#6B7280] flex-1 truncate">{r.nota}</span>}
      <div className="ml-auto flex gap-3 shrink-0">
        {reprogramando ? (
          <form onSubmit={handleReprogramar} className="flex items-center gap-2">
            <input
              type="date"
              value={nuevaFecha}
              onChange={(e) => setNuevaFecha(e.target.value)}
              className="border border-gray-200 rounded px-2 py-1 text-xs font-['Inter'] focus:outline-none focus:ring-1 focus:ring-[#0D2B5C]"
            />
            <button type="submit" disabled={!nuevaFecha} className="text-[#0D2B5C] hover:text-[#1E9BE8] font-['Raleway'] font-semibold text-xs transition-colors disabled:opacity-40">Guardar</button>
            <button type="button" onClick={() => setReprogramando(false)} className="text-[#6B7280] hover:text-[#0D2B5C] font-['Raleway'] text-xs">Cancelar</button>
          </form>
        ) : (
          <>
            {(r.estado === 'activo') && (
              <button onClick={() => update.mutate({ estado: 'resuelto' })} className="text-[#0D2B5C] hover:text-[#1E9BE8] font-['Raleway'] font-semibold text-xs transition-colors">
                Resolver
              </button>
            )}
            {(r.estado === 'activo' || r.estado === 'vencido') && (
              <button onClick={() => setReprogramando(true)} className="text-[#6B7280] hover:text-[#0D2B5C] font-['Raleway'] text-xs transition-colors">
                Reprogramar
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function ObservacionInline({ value, onSave }) {
  const [editando, setEditando] = useState(false);
  const [texto, setTexto] = useState(value);

  function handleBlur() {
    setEditando(false);
    if (texto !== value) onSave(texto);
  }

  return (
    <div>
      <span className="text-[#6B7280] text-sm font-['Inter']">Observación</span>
      {editando ? (
        <textarea
          autoFocus
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          onBlur={handleBlur}
          rows={3}
          className="mt-1 w-full border border-[#0D2B5C] rounded-md px-3 py-2 text-sm font-['Inter'] focus:outline-none focus:ring-2 focus:ring-[#0D2B5C] resize-none"
        />
      ) : (
        <p
          onClick={() => { setTexto(value); setEditando(true); }}
          className="mt-1 min-h-[2.5rem] text-sm font-['Inter'] text-[#0D2B5C] cursor-text rounded-md px-3 py-2 hover:bg-gray-50 border border-transparent hover:border-gray-200 transition-colors"
        >
          {value || <span className="text-gray-400">Agregar observación...</span>}
        </p>
      )}
    </div>
  );
}
